import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  afterNextRender,
  afterRenderEffect,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
  viewChildren,
} from '@angular/core';
import { ButtonComponent } from '../button/button.component';
import { IconButtonComponent } from '../button/icon-button.component';
import { SpinnerComponent } from '../feedback/spinner.component';
import { uniqueId } from '../form/ids';
import {
  CropState,
  INITIAL_CROP,
  MAX_ZOOM,
  MIN_ZOOM,
  Size,
  clampCrop,
  cropRect,
  drawTransform,
  panCrop,
  rotateCrop,
  sameCrop,
  zoomCrop,
} from './image-cropper.geometry';

export type ImageCropperStatus = 'empty' | 'loading' | 'ready' | 'error';

/** Lato in pixel del file esportato dal riquadro quadrato. */
export const CROP_EXPORT_SIZE = 1024;
/** Larghezza in pixel del file esportato dai riquadri rettangolari (16:9 → 1280 × 720). */
export const CROP_EXPORT_WIDE_WIDTH = 1280;
/** Rapporto dell'immagine del cantiere. */
export const SITE_IMAGE_ASPECT = 16 / 9;
/** Qualità del JPEG esportato. */
export const CROP_EXPORT_QUALITY = 0.92;
/** Lati delle anteprime circolari, in pixel CSS. */
export const CROP_PREVIEW_SIZES = [96, 40, 24] as const;
/** Altezza dell'anteprima dei riquadri rettangolari, in pixel CSS. */
const WIDE_PREVIEW_HEIGHT = 90;

/** Passo delle frecce, in lati del riquadro (con Maiusc il passo è cinque volte più lungo). */
const KEY_STEP = 0.02;
/** Fattore di zoom dei tasti + e − e dei pulsanti. */
const KEY_ZOOM = 1.15;
/** Sensibilità della rotella. */
const WHEEL_ZOOM = 0.0015;

interface Point {
  readonly x: number;
  readonly y: number;
}

/**
 * Editor della foto (research R-03, R-13). Per la foto del profilo il riquadro è quadrato (fino a
 * 440 px) con la maschera circolare; con `aspect` diverso da 1 (16:9 per l'immagine del cantiere)
 * il riquadro è rettangolare e il file esportato è largo 1280 px. La foto parte centrata e copre il
 * riquadro. Si sposta trascinando (mouse e
 * tocco) o con le frecce, si ingrandisce con il cursore, la rotella, due dita o i tasti + e −, si
 * ruota di 90°; "Centra" rimette la foto al centro, "Ripristina" annulla tutto. Le anteprime a
 * 96, 40 e 24 px e il file esportato (`export()`, JPEG 1024×1024) usano la stessa geometria
 * del riquadro, quindi coincidono con ciò che si vede.
 */
@Component({
  selector: 'bs-image-cropper',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonComponent, IconButtonComponent, SpinnerComponent],
  host: { class: 'block' },
  template: `
    <div class="flex flex-col gap-4" [attr.data-testid]="testId()" [attr.data-status]="status()">
      <div class="flex flex-col gap-4 sm:flex-row sm:items-start">
        <div
          #viewport
          class="relative w-full touch-none select-none overflow-hidden rounded-2xl bg-stone-200 outline-offset-2 focus-visible:outline-2 focus-visible:outline-ink dark:bg-stone-800 dark:focus-visible:outline-bsite-400"
          [class.cursor-grab]="ready()"
          [class.aspect-square]="square()"
          [class.max-w-110]="square()"
          [class.aspect-video]="!square()"
          [class.max-w-2xl]="!square()"
          role="group"
          aria-roledescription="area di ritaglio"
          [attr.aria-label]="label()"
          [attr.aria-describedby]="instructionsId"
          [attr.tabindex]="ready() ? 0 : -1"
          [attr.data-testid]="testId() ? testId() + '-area' : null"
          (pointerdown)="onPointerDown($event)"
          (pointermove)="onPointerMove($event)"
          (pointerup)="onPointerUp($event)"
          (pointercancel)="onPointerUp($event)"
          (wheel)="onWheel($event)"
          (keydown)="onKeydown($event)"
        >
          <canvas #stage class="absolute inset-0 size-full" [hidden]="!ready()"></canvas>
          @if (ready() && square()) {
            <svg
              class="pointer-events-none absolute inset-0 size-full"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <path
                class="fill-stone-950/60"
                fill-rule="evenodd"
                d="M0 0H100V100H0Z M50 0A50 50 0 1 1 50 100A50 50 0 1 1 50 0Z"
              />
              <circle
                cx="50"
                cy="50"
                r="49.6"
                fill="none"
                class="stroke-white/80"
                stroke-width="0.4"
              />
            </svg>
          } @else if (ready()) {
            <div
              class="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-inset ring-white/80"
              aria-hidden="true"
            ></div>
          } @else {
            <div
              class="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center text-sm text-stone-600 dark:text-stone-300"
            >
              @switch (status()) {
                @case ('loading') {
                  <bs-spinner label="Apertura della foto…" />
                }
                @case ('error') {
                  <p role="alert" class="font-medium text-red-700 dark:text-red-400">
                    Non riusciamo ad aprire questa immagine. Prova con una foto JPEG, PNG o WEBP.
                  </p>
                }
                @default {
                  <p>Scegli una foto per iniziare.</p>
                }
              }
            </div>
          }
        </div>

        <div class="flex flex-col gap-2">
          <p class="text-sm font-medium text-ink dark:text-stone-100">Anteprima</p>
          <div class="flex items-end gap-3" aria-hidden="true">
            @if (square()) {
              @for (size of previewSizes; track size) {
                <canvas
                  #preview
                  [attr.data-size]="size"
                  [style.width.px]="size"
                  [style.height.px]="size"
                  class="rounded-full bg-stone-200 ring-1 ring-stone-200 dark:bg-stone-800 dark:ring-stone-700"
                ></canvas>
              }
            } @else {
              <canvas
                #preview
                [attr.data-size]="widePreviewHeight"
                [style.width.px]="widePreviewHeight * aspect()"
                [style.height.px]="widePreviewHeight"
                class="rounded-lg bg-stone-200 ring-1 ring-stone-200 dark:bg-stone-800 dark:ring-stone-700"
              ></canvas>
            }
          </div>
          <p class="text-xs text-stone-500 dark:text-stone-400">
            {{ previewHint() }}
          </p>
        </div>
      </div>

      <p [id]="instructionsId" class="text-sm text-stone-600 dark:text-stone-300">
        Trascina la foto o usa le frecce per spostarla; usa il cursore, la rotella, due dita o i
        tasti + e − per lo zoom.
      </p>

      <div
        class="flex items-center gap-2"
        [class.max-w-110]="square()"
        [class.max-w-2xl]="!square()"
      >
        <bs-icon-button
          icon="zoom-out"
          label="Riduci lo zoom"
          [disabled]="!ready() || crop().zoom <= minZoom"
          (click)="zoomBy(1 / zoomStep)"
        />
        <input
          type="range"
          class="h-11 min-w-0 flex-1 cursor-pointer accent-ink disabled:cursor-not-allowed dark:accent-bsite-400"
          aria-label="Zoom"
          [attr.aria-valuetext]="zoomText()"
          [min]="minZoom"
          [max]="maxZoom"
          step="0.01"
          [value]="crop().zoom"
          [disabled]="!ready()"
          [attr.data-testid]="testId() ? testId() + '-zoom' : null"
          (input)="onZoomInput($event)"
        />
        <bs-icon-button
          icon="zoom-in"
          label="Aumenta lo zoom"
          [disabled]="!ready() || crop().zoom >= maxZoom"
          (click)="zoomBy(zoomStep)"
        />
        <span class="w-14 shrink-0 text-right font-mono text-sm text-stone-600 dark:text-stone-300">
          {{ zoomText() }}
        </span>
      </div>

      <div class="flex flex-wrap gap-2">
        <bs-button
          variant="secondary"
          icon="rotate-left"
          [disabled]="!ready()"
          [testId]="testId() ? testId() + '-rotate-left' : null"
          (click)="rotate('left')"
        >
          Ruota a sinistra
        </bs-button>
        <bs-button
          variant="secondary"
          icon="rotate-right"
          [disabled]="!ready()"
          [testId]="testId() ? testId() + '-rotate-right' : null"
          (click)="rotate('right')"
        >
          Ruota a destra
        </bs-button>
        <bs-button
          variant="ghost"
          icon="center"
          [disabled]="!ready()"
          [testId]="testId() ? testId() + '-center' : null"
          (click)="center()"
        >
          Centra
        </bs-button>
        <bs-button
          variant="ghost"
          icon="reset"
          [disabled]="!ready()"
          [testId]="testId() ? testId() + '-reset' : null"
          (click)="reset()"
        >
          Ripristina
        </bs-button>
      </div>
    </div>
  `,
})
export class ImageCropperComponent {
  private readonly document = inject(DOCUMENT);

  /** Foto scelta dall'utente; `null` mostra l'invito a sceglierne una. */
  readonly file = input<Blob | null>(null);
  /** Nome accessibile dell'area di ritaglio. */
  readonly label = input('Ritaglio della foto del profilo');
  /** Rapporto larghezza / altezza del riquadro: 1 per il profilo, 16/9 per il cantiere. */
  readonly aspect = input(1);
  /** Dove comparirà la foto, sotto l'anteprima. */
  readonly previewHint = input('Così comparirà nel profilo, negli elenchi e nei commenti.');
  readonly testId = input<string | null>(null);
  /** L'inquadratura è cambiata (spostamento, zoom, rotazione). */
  readonly changed = output<CropState>();

  private readonly bitmap = signal<ImageBitmap | null>(null);
  private readonly state = signal<CropState>(INITIAL_CROP);
  private readonly loadStatus = signal<ImageCropperStatus>('empty');
  private readonly width = signal<number | null>(null);
  private readonly height = signal<number | null>(null);
  private readonly viewportSize = signal(0);

  /** Stato del caricamento della foto. */
  readonly status = this.loadStatus.asReadonly();
  /** Larghezza della foto in pixel, dopo l'orientamento dei metadati. */
  readonly sourceWidth = this.width.asReadonly();
  /** Altezza della foto in pixel, dopo l'orientamento dei metadati. */
  readonly sourceHeight = this.height.asReadonly();
  /** Inquadratura corrente. */
  readonly crop = this.state.asReadonly();
  /** Lato corto dell'area inquadrata in pixel della foto: sotto 128 la foto risulterà sgranata. */
  readonly cropSourceSize = computed(() => {
    const source = this.source();
    return source ? cropRect(source, this.state(), this.aspect()).size : null;
  });

  protected readonly ready = computed(() => this.loadStatus() === 'ready');
  protected readonly square = computed(() => Math.abs(this.aspect() - 1) < 1e-6);
  protected readonly widePreviewHeight = WIDE_PREVIEW_HEIGHT;
  protected readonly minZoom = MIN_ZOOM;
  protected readonly maxZoom = MAX_ZOOM;
  protected readonly zoomStep = KEY_ZOOM;
  protected readonly previewSizes = CROP_PREVIEW_SIZES;
  protected readonly instructionsId = uniqueId('bs-image-cropper');
  protected readonly zoomText = computed(() => `${Math.round(this.state().zoom * 100)}%`);

  private readonly viewport = viewChild.required<ElementRef<HTMLElement>>('viewport');
  private readonly stage = viewChild.required<ElementRef<HTMLCanvasElement>>('stage');
  private readonly previews = viewChildren<ElementRef<HTMLCanvasElement>>('preview');
  private readonly pointers = new Map<number, Point>();
  private loadToken = 0;

  private readonly source = computed<Size | null>(() => {
    const width = this.width();
    const height = this.height();
    return width && height ? { width, height } : null;
  });

  constructor() {
    const destroyRef = inject(DestroyRef);

    effect(() => {
      const file = this.file();
      untracked(() => void this.load(file));
    });

    afterNextRender(() => {
      const element = this.viewport().nativeElement;
      const measure = () => this.viewportSize.set(element.clientWidth);
      measure();
      const view = this.document.defaultView;
      if (view && 'ResizeObserver' in view) {
        const observer = new view.ResizeObserver(measure);
        observer.observe(element);
        destroyRef.onDestroy(() => observer.disconnect());
      }
    });

    // Ridisegna riquadro e anteprime a ogni cambio di inquadratura, foto o misura.
    afterRenderEffect(() => {
      const bitmap = this.bitmap();
      const source = this.source();
      const state = this.state();
      const size = this.viewportSize();
      if (!bitmap || !source) {
        return;
      }
      this.paint(this.stage().nativeElement, size / this.aspect(), bitmap, source, state);
      for (const { nativeElement: canvas } of this.previews()) {
        this.paint(canvas, Number(canvas.dataset['size']), bitmap, source, state);
      }
    });

    destroyRef.onDestroy(() => {
      this.loadToken++;
      this.bitmap()?.close();
    });
  }

  /**
   * L'area inquadrata come JPEG di qualità 0,92: 1024×1024 nel quadrato, largo 1280 px nei
   * riquadri rettangolari. Rifiuta se non c'è una foto o se il browser non sa produrre il file.
   */
  async export(): Promise<Blob> {
    const bitmap = this.bitmap();
    const source = this.source();
    if (!bitmap || !source) {
      throw new Error('Nessuna foto da esportare.');
    }
    const aspect = this.aspect();
    const width = this.square() ? CROP_EXPORT_SIZE : CROP_EXPORT_WIDE_WIDTH;
    const height = this.square() ? CROP_EXPORT_SIZE : Math.round(CROP_EXPORT_WIDE_WIDTH / aspect);
    const canvas = this.document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Il browser non permette di elaborare la foto.');
    }
    drawCrop(context, bitmap, source, this.state(), height, width / height);
    return new Promise<Blob>((resolve, reject) =>
      canvas.toBlob(
        (blob) =>
          blob ? resolve(blob) : reject(new Error('Esportazione della foto non riuscita.')),
        'image/jpeg',
        CROP_EXPORT_QUALITY,
      ),
    );
  }

  /** "Centra": foto al centro e zoom di copertura, mantenendo la rotazione. */
  center(): void {
    this.update({ ...INITIAL_CROP, rotation: this.state().rotation });
  }

  /** "Ripristina": annulla spostamento, zoom e rotazione. */
  reset(): void {
    this.update(INITIAL_CROP);
  }

  rotate(direction: 'left' | 'right'): void {
    const source = this.source();
    if (source) {
      this.update(rotateCrop(source, this.state(), direction, this.aspect()));
    }
  }

  protected zoomBy(factor: number): void {
    const source = this.source();
    if (source) {
      this.update(zoomCrop(source, this.state(), this.state().zoom * factor, 0, 0, this.aspect()));
    }
  }

  protected onZoomInput(event: Event): void {
    const source = this.source();
    if (source) {
      const zoom = Number((event.target as HTMLInputElement).value);
      this.update(zoomCrop(source, this.state(), zoom, 0, 0, this.aspect()));
    }
  }

  protected onKeydown(event: KeyboardEvent): void {
    const source = this.source();
    if (!source || !this.ready()) {
      return;
    }
    const step = event.shiftKey ? KEY_STEP * 5 : KEY_STEP;
    const state = this.state();
    const aspect = this.aspect();
    let next: CropState | null = null;
    switch (event.key) {
      case 'ArrowLeft':
        next = panCrop(source, state, -step, 0, aspect);
        break;
      case 'ArrowRight':
        next = panCrop(source, state, step, 0, aspect);
        break;
      case 'ArrowUp':
        next = panCrop(source, state, 0, -step, aspect);
        break;
      case 'ArrowDown':
        next = panCrop(source, state, 0, step, aspect);
        break;
      case '+':
      case '=':
        next = zoomCrop(source, state, state.zoom * KEY_ZOOM, 0, 0, aspect);
        break;
      case '-':
      case '_':
        next = zoomCrop(source, state, state.zoom / KEY_ZOOM, 0, 0, aspect);
        break;
    }
    if (next) {
      event.preventDefault();
      this.update(next);
    }
  }

  protected onPointerDown(event: PointerEvent): void {
    if (!this.ready() || (event.pointerType === 'mouse' && event.button !== 0)) {
      return;
    }
    const element = this.viewport().nativeElement;
    element.setPointerCapture?.(event.pointerId);
    this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    event.preventDefault();
    element.focus({ preventScroll: true });
  }

  protected onPointerMove(event: PointerEvent): void {
    const previous = this.pointers.get(event.pointerId);
    const source = this.source();
    // Gli spostamenti si misurano in altezze del riquadro.
    const aspect = this.aspect();
    const size = (this.viewportSize() || this.viewport().nativeElement.clientWidth) / aspect;
    if (!previous || !source || size <= 0) {
      return;
    }
    const current = { x: event.clientX, y: event.clientY };
    if (this.pointers.size === 1) {
      this.pointers.set(event.pointerId, current);
      this.update(
        panCrop(
          source,
          this.state(),
          (current.x - previous.x) / size,
          (current.y - previous.y) / size,
          aspect,
        ),
      );
      return;
    }
    // Due dita: lo zoom segue la distanza, lo spostamento segue il punto medio.
    const [first, second] = [...this.pointers.entries()].slice(0, 2);
    const other = first[0] === event.pointerId ? second[1] : first[1];
    const before = { distance: distance(previous, other), middle: middle(previous, other) };
    this.pointers.set(event.pointerId, current);
    const after = { distance: distance(current, other), middle: middle(current, other) };
    if (before.distance <= 0) {
      return;
    }
    const anchor = this.toViewport(after.middle);
    const panned = panCrop(
      source,
      this.state(),
      (after.middle.x - before.middle.x) / size,
      (after.middle.y - before.middle.y) / size,
      aspect,
    );
    this.update(
      zoomCrop(
        source,
        panned,
        panned.zoom * (after.distance / before.distance),
        anchor.x,
        anchor.y,
        aspect,
      ),
    );
  }

  protected onPointerUp(event: PointerEvent): void {
    this.pointers.delete(event.pointerId);
  }

  protected onWheel(event: WheelEvent): void {
    const source = this.source();
    if (!source || !this.ready()) {
      return;
    }
    event.preventDefault();
    const anchor = this.toViewport({ x: event.clientX, y: event.clientY });
    const state = this.state();
    this.update(
      zoomCrop(
        source,
        state,
        state.zoom * Math.exp(-event.deltaY * WHEEL_ZOOM),
        anchor.x,
        anchor.y,
        this.aspect(),
      ),
    );
  }

  /** Punto dello schermo in altezze del riquadro, misurate dal suo centro. */
  private toViewport(point: Point): Point {
    const rect = this.viewport().nativeElement.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
      return { x: 0, y: 0 };
    }
    return {
      x: (point.x - rect.left - rect.width / 2) / rect.height,
      y: (point.y - rect.top - rect.height / 2) / rect.height,
    };
  }

  private update(next: CropState): void {
    const source = this.source();
    const clamped = source ? clampCrop(source, next, this.aspect()) : next;
    if (!sameCrop(clamped, this.state())) {
      this.state.set(clamped);
      this.changed.emit(clamped);
    }
  }

  private async load(file: Blob | null): Promise<void> {
    const token = ++this.loadToken;
    this.pointers.clear();
    this.bitmap()?.close();
    this.bitmap.set(null);
    this.width.set(null);
    this.height.set(null);
    this.state.set(INITIAL_CROP);
    if (!file) {
      this.loadStatus.set('empty');
      return;
    }
    const view = this.document.defaultView;
    if (!view || typeof view.createImageBitmap !== 'function') {
      this.loadStatus.set('error');
      return;
    }
    this.loadStatus.set('loading');
    try {
      // L'orientamento EXIF si applica qui: la foto dello smartphone non compare coricata.
      const bitmap = await view.createImageBitmap(file, { imageOrientation: 'from-image' });
      if (token !== this.loadToken) {
        bitmap.close();
        return;
      }
      if (bitmap.width <= 0 || bitmap.height <= 0) {
        bitmap.close();
        this.loadStatus.set('error');
        return;
      }
      this.bitmap.set(bitmap);
      this.width.set(bitmap.width);
      this.height.set(bitmap.height);
      this.loadStatus.set('ready');
    } catch {
      if (token === this.loadToken) {
        this.loadStatus.set('error');
      }
    }
  }

  /** Disegna su un canvas alto `cssHeight` pixel CSS, largo secondo il rapporto. */
  private paint(
    canvas: HTMLCanvasElement,
    cssHeight: number,
    bitmap: ImageBitmap,
    source: Size,
    state: CropState,
  ): void {
    if (!(cssHeight > 0)) {
      return;
    }
    const aspect = this.aspect();
    const ratio = this.document.defaultView?.devicePixelRatio ?? 1;
    const height = Math.max(1, Math.round(cssHeight * ratio));
    const width = Math.max(1, Math.round(height * aspect));
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }
    const context = canvas.getContext('2d');
    if (context) {
      drawCrop(context, bitmap, source, state, height, aspect);
    }
  }
}

/**
 * Disegna l'inquadratura su un rettangolo alto `height` pixel e largo `height × aspect`, con il
 * fondo bianco (il JPEG non ha trasparenza).
 */
function drawCrop(
  context: CanvasRenderingContext2D,
  bitmap: CanvasImageSource,
  source: Size,
  state: CropState,
  height: number,
  aspect: number,
): void {
  const transform = drawTransform(source, state, height, aspect);
  context.save();
  context.setTransform(1, 0, 0, 1, 0, 0);
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, Math.round(height * aspect), height);
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  context.translate(transform.translateX, transform.translateY);
  context.rotate(transform.rotation);
  context.scale(transform.scale, transform.scale);
  context.drawImage(bitmap, -source.width / 2, -source.height / 2);
  context.restore();
}

function distance(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function middle(a: Point, b: Point): Point {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}
